/**
 * EpcTeamFlowDiagram — "Live Execution Engine" visual language adapted to the
 * EPC project Team Hierarchy tab.
 *
 * Implements the design from `EPC System Flow.html`:
 *   • Three vertical zones — Control (PMO), Coordination (Dept Heads + Managers),
 *     Execution (Site Engineers).
 *   • Sovereign Emerald aesthetic: cream banknote-paper canvas, 1px engraved
 *     racing-green hairline connectors, soft ink shadows.
 *   • Curved SVG connectors between tiers.
 *   • PMO racing-green node, dept-colored avatars, engineer cards with mobile-device frame
 *     and a status dot.
 *   • Hover tooltips for every node.
 *   • Ambient particles drift up the engineer→manager edges to give a "live" feel.
 *   • Drop-in replacement for the existing `EpcHierarchyDiagram` — same prop
 *     signature so the parent workspace doesn't change.
 */

import {
  Activity,
  AlertTriangle,
  Building2,
  GitBranch,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import type {
  EpcProjectMembershipRow,
  EpcProjectOrgUnit,
  EpcProjectTeamRole,
} from '../../../../services/epcProjectService';
import type { DepartmentHierarchy } from './hierarchyLayout';
import { initials } from './hierarchyLayout';
import { ROLE_LABEL } from './teamCapabilities';
import type { TeamCapability } from './teamCapabilities';

/* ───────────────────────── PROP / INTENT TYPES ── */

export type DiagramAddIntent =
  | { kind: 'governance' }
  | { kind: 'department_head'; orgId: string }
  | { kind: 'manager'; orgId: string; reportsToUserId: string }
  | { kind: 'site_engineer'; orgId: string; managerUserId: string };

/* ───────────────────────── DESIGN TOKENS ──
   Lifted from `EPC System Flow.html` :root vars, kept inline so the
   component is self-contained and doesn't rely on a global theme. */

const TOKEN = {
  bg0: '#F8F2E2',
  bg1: '#FFFEFB',
  bg2: '#F2EADB',
  line: 'rgba(11,59,46,0.14)',
  lineStrong: 'rgba(11,59,46,0.28)',
  ink: '#0B3B2E',
  ink2: '#2A1F17',
  ink3: '#7C8A82',
  accent: '#C9A961',
  good: '#339274',
  bad: '#B91C1C',
  blue: '#1F5A47',
};

/* Department palette — muted engraving inks (readable as avatar fills with white text on cream). */
const DEPT_PALETTE = ['#1F5A47', '#8A5A08', '#3E5C76', '#7A4A2B', '#9F1239', '#339274', '#5A6275', '#584B8E'];

/* ───────────────────────── LAYOUT MODEL ── */

type EngLayout = {
  id: string;
  member: EpcProjectMembershipRow;
  x: number;
  y: number;
  mgrId: string;
  color: string;
};

type MgrLayout = {
  id: string;
  member: EpcProjectMembershipRow;
  x: number;
  y: number;
  deptId: string;
  color: string;
  engineers: EngLayout[];
  /** the "manager-to-engineer add slot" position (if allowed) */
  addEngineerAt?: { x: number; y: number } | null;
  /** can the viewer act on the vacant engineer slot? (slot itself renders by capacity) */
  addEngineerCanAct: boolean;
  /** dotted "manager team" sub-box (manager card + their engineers) */
  boxLeft: number;
  boxTop: number;
  boxWidth: number;
  boxHeight: number;
};

type DeptLayout = {
  id: string;
  org: EpcProjectOrgUnit;
  orgId: string;
  /** centered x of this department subtree */
  x: number;
  y: number;
  color: string;
  initial: string;
  head?: EpcProjectMembershipRow;
  managers: MgrLayout[];
  /** subtree content width (managers + engineers) */
  width: number;
  /** outer dotted-box bounds (uniform top/bottom across all depts) */
  boxLeft: number;
  boxTop: number;
  boxWidth: number;
  boxHeight: number;
  /** add-manager slot under the dept head */
  addManagerAt?: { x: number; y: number } | null;
  /** can the viewer act on the vacant manager slot? (slot itself renders by capacity) */
  addManagerCanAct: boolean;
};

type GovLayout = {
  id: string;
  member: EpcProjectMembershipRow;
  x: number;
  y: number;
  /** is this the central PMO-style anchor? */
  primary: boolean;
};

type Layout = {
  width: number;
  height: number;
  primary: GovLayout | null;
  satellites: GovLayout[]; // other governance shown as small chips left/right of PMO
  departments: DeptLayout[];
  /** absolute centers of the three zone bands (for left labels) */
  yControl: number;
  yDept: number;
  yMgr: number;
  yEng: number;
};

/* ───────────────────────── LAYOUT COMPUTATION ── */

const ENG_W = 124;        // width of engineer card
const ENG_GAP_X = 16;     // horizontal gap between engineer cards
const MGR_GAP_X = 36;     // horizontal gap between manager subtrees
const DEPT_GAP_X = 34;    // gap *between department box edges*
const BOX_PAD_X = 22;     // horizontal padding inside each department box
const ENG_ROW_GAP_Y = 56; // vertical stagger between two engineer rows

const Y_CONTROL = 110;
const Y_DEPT = 250;
const Y_MGR = 380;
const Y_ENG = 540;

/* Tier caps (spec §3.1): 1 head per dept · 3 managers per dept · 20 engineers per manager. */
export const MAX_MANAGERS_PER_DEPT = 3;
export const MAX_ENGINEERS_PER_MANAGER = 20;

/** Engineer grid cells for a manager = engineers + one vacant slot while under cap. */
function managerCellCount(engineerCount: number): number {
  return engineerCount + (engineerCount < MAX_ENGINEERS_PER_MANAGER ? 1 : 0);
}

/* Department box top/bottom (uniform across all depts so boxes align). */
const BOX_TOP = Y_DEPT - 56;                         // ≈ 194
const BOX_BOTTOM = Y_ENG + ENG_ROW_GAP_Y + 38;       // ≈ 634
const BOX_HEIGHT = BOX_BOTTOM - BOX_TOP;             // ≈ 440

/* Manager-team sub-box (nested inside the department box). */
const MGR_BOX_PAD_X = 10;
const MGR_BOX_TOP = Y_MGR - 32;                      // ≈ 348
const MGR_BOX_BOTTOM = Y_ENG + ENG_ROW_GAP_Y + 30;   // ≈ 626
const MGR_BOX_HEIGHT = MGR_BOX_BOTTOM - MGR_BOX_TOP; // ≈ 278

function deptInitial(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Manager subtree width — sized for the *total* engineer-row cells (real + add). */
function managerSubtreeWidth(totalCells: number): number {
  if (totalCells <= 1) return Math.max(140, ENG_W);
  /* cells stagger across two rows — width = ceil(N/2) cards in the wider row */
  const perRow = Math.ceil(totalCells / 2);
  return perRow * ENG_W + (perRow - 1) * ENG_GAP_X;
}

/** Position the i-th engineer cell (row-stagger packing). */
function engineerCellAt(
  mgrCx: number,
  i: number,
  totalCells: number,
): { x: number; y: number } {
  const perRow = Math.max(1, Math.ceil(totalCells / 2));
  const rowW = perRow * ENG_W + (perRow - 1) * ENG_GAP_X;
  const rowStart = mgrCx - rowW / 2;
  const row = i % 2;
  const col = Math.floor(i / 2);
  return {
    x: rowStart + col * (ENG_W + ENG_GAP_X) + ENG_W / 2,
    y: Y_ENG + row * ENG_ROW_GAP_Y,
  };
}

/** Department subtree width = manager subtrees (each incl. its vacant engineer cell) + vacant manager column while under cap. */
function departmentSubtreeWidth(d: DepartmentHierarchy): number {
  const mgrWidths = d.managers.map((m) => managerSubtreeWidth(managerCellCount(m.engineers.length)));
  if (d.head && d.managers.length < MAX_MANAGERS_PER_DEPT) mgrWidths.push(160);
  if (mgrWidths.length === 0) return 220;
  const total = mgrWidths.reduce((a, b) => a + b, 0);
  const gaps = (mgrWidths.length - 1) * MGR_GAP_X;
  return Math.max(220, total + gaps);
}

function buildLayout(
  width: number,
  governance: EpcProjectMembershipRow[],
  departments: DepartmentHierarchy[],
  canAddManagerForOrg: (orgId: string) => boolean,
  canAddEngineerForManager: (orgId: string, mgrUserId: string) => boolean,
): Layout {
  /* ---- governance ---- */
  /* Pick a primary node — the PMO anchors the Control layer; every other
     governance member (EPC Head included) renders as a same-level chip
     beside it, never above it. */
  const order: EpcProjectTeamRole[] = ['pmo', 'epc_head', 'project_head', 'project_coordinator'];
  const sortedGov = [...governance].sort(
    (a, b) => order.indexOf(a.role) - order.indexOf(b.role),
  );
  const primaryMember = sortedGov[0] ?? null;
  const satelliteMembers = sortedGov.slice(1);

  /* ---- departments laid out left-to-right ---- */
  /* Each dept's "cell" = subtree width + 2 × box padding. */
  const deptSubtreeWidths = departments.map((d) => departmentSubtreeWidth(d));
  const deptCellWidths = deptSubtreeWidths.map((w) => w + BOX_PAD_X * 2);
  const totalCellW = deptCellWidths.reduce((a, b) => a + b, 0);
  const totalGapW = Math.max(0, departments.length - 1) * DEPT_GAP_X;
  const OUTER_MARGIN = 60;
  const requiredCanvas = Math.max(width, totalCellW + totalGapW + OUTER_MARGIN * 2);
  const startX = Math.max(
    OUTER_MARGIN,
    (requiredCanvas - (totalCellW + totalGapW)) / 2,
  );

  let cursorX = startX;
  let maxContentBottom = Y_DEPT + 40;
  const deptLayouts: DeptLayout[] = departments.map((d, i) => {
    const w = deptSubtreeWidths[i]!;
    const cellW = deptCellWidths[i]!;
    const boxLeft = cursorX;
    const cx = cursorX + cellW / 2;
    const color = DEPT_PALETTE[i % DEPT_PALETTE.length]!;

    /* ---- managers row inside this dept (vacant slots reserved by capacity) ---- */
    const showVacantManager = Boolean(d.head) && d.managers.length < MAX_MANAGERS_PER_DEPT;
    const mgrCount = d.managers.length + (showVacantManager ? 1 : 0);
    const mgrWidths = d.managers.map((m) => managerSubtreeWidth(managerCellCount(m.engineers.length)));
    if (showVacantManager) mgrWidths.push(160);
    const mgrTotalW = mgrWidths.reduce((a, b) => a + b, 0);
    const mgrGaps = Math.max(0, mgrCount - 1) * MGR_GAP_X;
    const mgrStart = cx - (mgrTotalW + mgrGaps) / 2;

    let mgrCursorX = mgrStart;
    const managers: MgrLayout[] = d.managers.map((mb) => {
      const showVacantEng = mb.engineers.length < MAX_ENGINEERS_PER_MANAGER;
      const totalCells = mb.engineers.length + (showVacantEng ? 1 : 0);
      const subW = managerSubtreeWidth(totalCells);
      const mgrCx = mgrCursorX + subW / 2;

      /* engineers + (optional) vacant engineer slot share the same staggered grid,
         so they never overlap and the manager subtree width is sized for them. */
      const engs: EngLayout[] = mb.engineers.map((eMember, idx) => {
        const pos = engineerCellAt(mgrCx, idx, totalCells);
        return {
          id: 'eng-' + eMember.id,
          member: eMember,
          x: pos.x,
          y: pos.y,
          mgrId: 'mgr-' + mb.manager.id,
          color,
        };
      });

      const addEng = showVacantEng ? engineerCellAt(mgrCx, mb.engineers.length, totalCells) : null;

      const mgrTwoRows = totalCells >= 2;
      const mgrBoxBottom = Y_ENG + (mgrTwoRows ? ENG_ROW_GAP_Y : 0) + 30;

      const mgr: MgrLayout = {
        id: 'mgr-' + mb.manager.id,
        member: mb.manager,
        x: mgrCx,
        y: Y_MGR,
        deptId: d.orgId,
        color,
        engineers: engs,
        addEngineerAt: addEng,
        addEngineerCanAct: canAddEngineerForManager(d.orgId, mb.manager.userId),
        boxLeft: mgrCx - subW / 2 - MGR_BOX_PAD_X,
        boxTop: MGR_BOX_TOP,
        boxWidth: subW + MGR_BOX_PAD_X * 2,
        boxHeight: mgrBoxBottom - MGR_BOX_TOP,
      };
      mgrCursorX += subW + MGR_GAP_X;
      return mgr;
    });

    let addManagerAt: { x: number; y: number } | null = null;
    if (showVacantManager) addManagerAt = { x: mgrCursorX + 80, y: Y_MGR };

    /* Dynamic box height — fit the deepest tier this department actually uses
       (head → managers → engineers) instead of a uniform full-height box, so
       departments with no managers/engineers don't render large empty space. */
    const deptHasMgrTier = mgrCount > 0;
    const deptHasEngTier = d.managers.length > 0;
    const deptEngTwoRows = d.managers.some(
      (m) => m.engineers.length + (m.engineers.length < MAX_ENGINEERS_PER_MANAGER ? 1 : 0) >= 2,
    );
    let deptContentBottom = Y_DEPT + 40;
    if (deptHasMgrTier) deptContentBottom = Math.max(deptContentBottom, Y_MGR + 46);
    if (deptHasEngTier)
      deptContentBottom = Math.max(
        deptContentBottom,
        Y_ENG + (deptEngTwoRows ? ENG_ROW_GAP_Y : 0) + 42,
      );
    const deptBoxBottom = deptContentBottom + 14;
    if (deptBoxBottom > maxContentBottom) maxContentBottom = deptBoxBottom;

    cursorX += cellW + DEPT_GAP_X;
    return {
      id: 'dept-' + d.orgId,
      org: d.org,
      orgId: d.orgId,
      x: cx,
      y: Y_DEPT,
      color,
      initial: deptInitial(d.org.name),
      head: d.head,
      managers,
      width: w,
      boxLeft,
      boxTop: BOX_TOP,
      boxWidth: cellW,
      boxHeight: deptBoxBottom - BOX_TOP,
      addManagerAt,
      addManagerCanAct: canAddManagerForOrg(d.orgId),
    };
  });

  /* ---- governance ---- */
  const canvasMidX = requiredCanvas / 2;
  const primary: GovLayout | null = primaryMember
    ? { id: 'gov-' + primaryMember.id, member: primaryMember, x: canvasMidX, y: Y_CONTROL, primary: true }
    : null;

  const sat: GovLayout[] = satelliteMembers.map((m, i) => ({
    id: 'gov-' + m.id,
    member: m,
    x: canvasMidX + (i % 2 === 0 ? -1 : 1) * (160 + Math.floor(i / 2) * 110),
    y: Y_CONTROL,
    primary: false,
  }));

  /* canvas height — fit the deepest department content (avoids a tall empty canvas
     when no department reaches the engineer tier). */
  const height = Math.max(Y_MGR + 110, maxContentBottom + 44);

  return {
    width: requiredCanvas,
    height,
    primary,
    satellites: sat,
    departments: deptLayouts,
    yControl: Y_CONTROL,
    yDept: Y_DEPT,
    yMgr: Y_MGR,
    yEng: Y_ENG,
  };
}

/* ───────────────────────── SVG curve helper ── */

function curve(x1: number, y1: number, x2: number, y2: number): string {
  const dy = y2 - y1;
  const cy1 = y1 + dy * 0.5;
  const cy2 = y2 - dy * 0.5;
  return `M ${x1} ${y1} C ${x1} ${cy1}, ${x2} ${cy2}, ${x2} ${y2}`;
}

/* ───────────────────────── PRIMITIVES ── */

function Avatar({
  name,
  uid,
  size = 28,
  color,
}: {
  name: string | null | undefined;
  uid: string;
  size?: number;
  color: string;
}) {
  const av = initials(name ?? undefined, uid);
  return (
    <div
      style={{
        width: size,
        height: size,
        background: color,
        fontSize: size * 0.36,
      }}
      className="flex shrink-0 items-center justify-center rounded-md font-semibold text-white"
    >
      {av}
    </div>
  );
}

/* Engineer mobile-device frame icon (matches the design's `.device-frame`). */
function DeviceFrame({ tone = 'idle' }: { tone?: 'idle' | 'active' | 'done' | 'delayed' }) {
  const border =
    tone === 'active' ? TOKEN.blue : tone === 'done' ? TOKEN.good : tone === 'delayed' ? TOKEN.bad : TOKEN.ink3;
  const fill =
    tone === 'active' ? TOKEN.blue : tone === 'done' ? TOKEN.good : tone === 'delayed' ? TOKEN.bad : TOKEN.ink3;
  const screenH = tone === 'idle' ? '60%' : tone === 'delayed' ? '70%' : tone === 'done' ? '95%' : '90%';

  return (
    <div
      className="relative flex shrink-0 items-end"
      style={{
        width: 14,
        height: 18,
        border: `1.2px solid ${border}`,
        borderRadius: 2.5,
        padding: 1.5,
      }}
    >
      <span
        className="absolute"
        style={{
          width: 4,
          height: 1,
          background: border,
          top: -2,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
      <span
        className="block w-full rounded-[1px]"
        style={{
          height: screenH,
          background: fill,
          transition: 'height 0.3s, background 0.3s',
        }}
      />
    </div>
  );
}

/* Reusable absolute card wrapper centred on (x,y). */
function AbsCard({
  x,
  y,
  className,
  style,
  onMouseEnter,
  onMouseLeave,
  onClick,
  children,
}: {
  x: number;
  y: number;
  className?: string;
  style?: React.CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      className={twMerge('absolute', className)}
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ───────────────────────── VACANT SLOT ── */

function VacantSlot({
  x,
  y,
  label,
  vacantLabel,
  capacity,
  width = 140,
  onClick,
  disabled,
  interactive,
}: {
  x: number;
  y: number;
  label: string;
  /** Shown instead of `label` when the viewer cannot act on the slot. Defaults to `label`. */
  vacantLabel?: string;
  /** e.g. "1/3" — rendered as a small tabular chip. */
  capacity?: string;
  width?: number;
  onClick?: () => void;
  disabled?: boolean;
  interactive: boolean;
}) {
  if (!interactive) {
    return (
      <AbsCard x={x} y={y}>
        <span
          className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed bg-[rgba(255,254,251,0.55)] px-3 py-2 text-[11px] font-medium text-[#7C8A82]"
          style={{ borderColor: TOKEN.line, width }}
        >
          {vacantLabel ?? label}
          {capacity ? (
            <span className="rounded bg-[#F2EADB] px-1 text-[10px] font-semibold tabular-nums text-[#7C8A82]">
              {capacity}
            </span>
          ) : null}
        </span>
      </AbsCard>
    );
  }
  return (
    <AbsCard x={x} y={y}>
      <button
        type="button"
        disabled={disabled || !onClick}
        onClick={onClick}
        className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed bg-[rgba(255,254,251,0.75)] px-3 py-2 text-[11px] font-medium text-[#7C8A82] transition-colors hover:border-[rgba(11,59,46,0.45)] hover:bg-[#FFFEFB] hover:text-[#1F5A47] disabled:cursor-not-allowed disabled:opacity-40"
        style={{ borderColor: TOKEN.lineStrong, width }}
      >
        <Plus className="h-3 w-3" /> {label}
        {capacity ? (
          <span className="rounded bg-[#F2EADB] px-1 text-[10px] font-semibold tabular-nums">{capacity}</span>
        ) : null}
      </button>
    </AbsCard>
  );
}

/* ───────────────────────── TOOLTIP ── */

type HoverData = {
  x: number;
  y: number;
  name: string;
  role: string;
  rows: Array<{ k: string; v: string }>;
} | null;

function Tooltip({ hover }: { hover: HoverData }) {
  if (!hover) return null;
  return (
    <div
      className="pointer-events-none absolute z-50 rounded-lg px-3 py-2 text-[11px] leading-snug text-white shadow-xl"
      style={{
        left: hover.x,
        top: hover.y - 14,
        transform: 'translate(-50%, -100%)',
        background: TOKEN.ink,
        boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 16px 40px -24px rgba(11,59,46,0.30)',
      }}
    >
      <div>
        <span className="font-semibold">{hover.name}</span>
        <span className="opacity-60"> · {hover.role}</span>
      </div>
      {hover.rows.map((r) => (
        <div key={r.k} className="mt-0.5 flex justify-between gap-5">
          <span className="opacity-60">{r.k}</span>
          <span className="font-semibold tabular-nums">{r.v}</span>
        </div>
      ))}
      <span
        className="absolute"
        style={{
          left: '50%',
          bottom: -4,
          width: 8,
          height: 8,
          background: TOKEN.ink,
          transform: 'translateX(-50%) rotate(45deg)',
        }}
      />
    </div>
  );
}

/* ───────────────────────── AMBIENT PARTICLES ──
   Drift small grey dots up the engineer→manager edges to give a "live" feel,
   exactly like the original design's ambientLoop(). */

type Particle = {
  id: number;
  pathId: string;
  born: number;
  dur: number;
  reverse: boolean;
  color: string;
  r: number;
};

function AmbientParticles({ particles, svgRef }: { particles: Particle[]; svgRef: React.RefObject<SVGSVGElement> }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      setTick((t) => (t + 1) % 10000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const now = performance.now();
  return (
    <>
      {particles.map((p) => {
        const path = svgRef.current?.querySelector<SVGPathElement>(`#${p.pathId}`);
        if (!path) return null;
        const len = path.getTotalLength();
        const t = Math.min(1, (now - p.born) / p.dur);
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const at = p.reverse ? len * (1 - eased) : len * eased;
        const pt = path.getPointAtLength(at);
        return (
          <circle
            key={p.id}
            cx={pt.x}
            cy={pt.y}
            r={p.r}
            fill={p.color}
            style={{ filter: 'drop-shadow(0 0 3px rgba(201,169,97,0.4))' }}
          />
        );
      })}
    </>
  );
}

/* ───────────────────────── MAIN ── */

export function EpcTeamFlowDiagram({
  governance,
  departments,
  cap,
  showGovernanceChrome,
  canGovernanceAdd,
  canAddHead,
  canAddManagerForOrg,
  canAddEngineerForManager,
  onIntent,
  onRemoveMember,
  canRemoveMember,
  saving,
  showEditFocus,
  onEditFocus,
  onOpenAddMember,
  onOpenAddDepartment,
  showAddMemberButton,
  showAddDepartmentButton,
  onCardClick,
  memberDeptCountMap,
  onRemoveDepartment,
}: {
  governance: EpcProjectMembershipRow[];
  departments: DepartmentHierarchy[];
  cap: TeamCapability;
  showGovernanceChrome: boolean;
  canGovernanceAdd: boolean;
  canAddHead: (orgId: string) => boolean;
  canAddManagerForOrg: (orgId: string) => boolean;
  canAddEngineerForManager: (orgId: string, managerUserId: string) => boolean;
  onIntent: (intent: DiagramAddIntent) => void;
  onRemoveMember: (m: EpcProjectMembershipRow) => void;
  canRemoveMember: (m: EpcProjectMembershipRow) => boolean;
  saving: boolean;
  showEditFocus?: boolean;
  onEditFocus?: (org: EpcProjectOrgUnit) => void;
  onOpenAddMember?: () => void;
  onOpenAddDepartment?: () => void;
  showAddMemberButton?: boolean;
  showAddDepartmentButton?: boolean;
  onCardClick?: (m: EpcProjectMembershipRow) => void;
  memberDeptCountMap: Map<string, number>;
  /** Optional: remove an entire department (soft delete via `inactive: true`). */
  onRemoveDepartment?: (org: EpcProjectOrgUnit) => void;
}) {
  const viewerOnly = cap === 'viewer' || cap === 'outsider';
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerW, setContainerW] = useState(1200);
  const [hover, setHover] = useState<HoverData>(null);
  const [isFullView, setIsFullView] = useState(false);

  /* Confirmation drawer state — used for both member and department removal. */
  type ConfirmTarget =
    | {
        kind: 'member';
        member: EpcProjectMembershipRow;
        roleLabel: string;
        deptName?: string;
      }
    | {
        kind: 'department';
        org: EpcProjectOrgUnit;
        memberCount: number;
      };
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);

  const askConfirm = useCallback((t: ConfirmTarget) => setConfirmTarget(t), []);
  const closeConfirm = useCallback(() => setConfirmTarget(null), []);
  const runConfirm = useCallback(() => {
    if (!confirmTarget) return;
    if (confirmTarget.kind === 'member') {
      onRemoveMember(confirmTarget.member);
    } else if (confirmTarget.kind === 'department' && onRemoveDepartment) {
      onRemoveDepartment(confirmTarget.org);
    }
    setConfirmTarget(null);
  }, [confirmTarget, onRemoveMember, onRemoveDepartment]);

  /* Esc exits full view (skip while a form field is focused). */
  useEffect(() => {
    if (!isFullView) return;
    const handler = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      const tag = (ev.target as HTMLElement | null)?.tagName?.toLowerCase();
      const editing =
        tag === 'input' || tag === 'textarea' || tag === 'select' ||
        (ev.target as HTMLElement | null)?.isContentEditable;
      if (editing) return;
      ev.preventDefault();
      setIsFullView(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullView]);

  /* Lock body scroll while full view is on. */
  useEffect(() => {
    if (!isFullView) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isFullView]);

  /* total members + dept count for the header. */
  const totalMembers =
    governance.length +
    departments.reduce(
      (s, d) =>
        s + (d.head ? 1 : 0) + d.managers.reduce((ms, m) => ms + 1 + m.engineers.length, 0),
      0,
    );

  /* Track container width for layout. */
  useLayoutEffect(() => {
    if (!canvasRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerW(Math.floor(e.contentRect.width));
    });
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(
    () => buildLayout(containerW, governance, departments, canAddManagerForOrg, canAddEngineerForManager),
    [containerW, governance, departments, canAddManagerForOrg, canAddEngineerForManager],
  );

  /* ---- ambient particles loop ---- */
  const [particles, setParticles] = useState<Particle[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    /* gather all engineer→manager and dept→manager edges to drift along */
    const collect = () => {
      const ids: string[] = [];
      for (const d of layout.departments) {
        for (const m of d.managers) {
          ids.push(`edge-mgr-${m.id}`);
          for (const e of m.engineers) ids.push(`edge-eng-${e.id}`);
        }
      }
      return ids;
    };

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const ids = collect();
      if (ids.length > 0 && Math.random() < 0.55) {
        const pathId = ids[Math.floor(Math.random() * ids.length)]!;
        const id = ++idRef.current;
        const dur = 900 + Math.random() * 500;
        const newParticle: Particle = {
          id,
          pathId,
          born: performance.now(),
          dur,
          reverse: true, // bottom-up, like the design's ambientLoop
          color: TOKEN.accent,
          r: 2,
        };
        setParticles((prev) => [...prev, newParticle]);
        window.setTimeout(() => {
          setParticles((prev) => prev.filter((p) => p.id !== id));
        }, dur + 60);
      }
      window.setTimeout(tick, 700 + Math.random() * 800);
    };
    const t = window.setTimeout(tick, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [layout]);

  /* ---- helpers ---- */
  const showTip = useCallback(
    (
      x: number,
      y: number,
      member: EpcProjectMembershipRow,
      role: string,
      extras: Array<{ k: string; v: string }> = [],
    ) => {
      const dCount = memberDeptCountMap.get(member.userId) ?? 1;
      const rows: Array<{ k: string; v: string }> = [];
      if (member.userEmail) rows.push({ k: 'Email', v: member.userEmail });
      if (dCount > 1) rows.push({ k: 'Departments', v: String(dCount) });
      rows.push(...extras);
      setHover({ x, y, name: member.userName ?? 'Unknown', role, rows });
    },
    [memberDeptCountMap],
  );

  const hideTip = useCallback(() => setHover(null), []);

  /* ---- render ---- */
  return (
    <div
      className={twMerge(
        isFullView
          ? 'fixed inset-0 z-[120] flex flex-col overflow-auto border-0 bg-[#FFFEFB]'
          : 'overflow-hidden rounded-xl border bg-[#FFFEFB]',
      )}
      style={{ borderColor: TOKEN.line, fontFamily: '"Geist", system-ui, sans-serif' }}
    >
      {/* Top bar — matches the design's `.topbar`. */}
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5"
        style={{
          borderColor: TOKEN.line,
          background: 'rgba(255,254,251,0.88)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-md text-white"
            style={{ background: TOKEN.ink }}
          >
            <GitBranch className="h-3.5 w-3.5" />
          </div>
          <div className="text-[13px] font-semibold tracking-tight" style={{ color: TOKEN.ink }}>
            Team Hierarchy
          </div>
          <div className="hidden text-[12px] sm:block" style={{ color: TOKEN.ink3 }}>
            ·{' '}
            <span style={{ color: TOKEN.ink2 }}>
              {totalMembers} {totalMembers === 1 ? 'member' : 'members'} · {departments.length}{' '}
              {departments.length === 1 ? 'department' : 'departments'}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsFullView((v) => !v)}
          className={twMerge(
            'ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11.5px] font-medium transition-colors',
            isFullView
              ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white hover:bg-[#2d5c47]'
              : 'border-[rgba(11,59,46,0.18)] bg-[#FFFEFB] text-[#2A1F17] hover:bg-[#F2EADB]',
          )}
          title={isFullView ? 'Exit full view (Esc)' : 'Open team hierarchy in full view'}
        >
          {isFullView ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          {isFullView ? 'Exit full view' : 'Full view'}
        </button>
        {showAddDepartmentButton && onOpenAddDepartment ? (
          <button
            type="button"
            disabled={saving}
            onClick={onOpenAddDepartment}
            className="inline-flex items-center gap-1.5 rounded-md border bg-[#FFFEFB] px-2.5 py-1.5 text-[11.5px] font-medium hover:bg-[#F2EADB] disabled:opacity-50"
            style={{ borderColor: TOKEN.line, color: TOKEN.ink2 }}
          >
            <Building2 className="h-3.5 w-3.5" />
            Add Department
          </button>
        ) : null}
        {showAddMemberButton && onOpenAddMember ? (
          <button
            type="button"
            disabled={saving}
            onClick={onOpenAddMember}
            className="inline-flex items-center gap-1.5 rounded-md border bg-[#264f3c] px-2.5 py-1.5 text-[11.5px] font-medium text-white hover:bg-[#2d5c47] disabled:opacity-50"
            style={{ borderColor: 'rgba(11,59,46,0.45)' }}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add Member
          </button>
        ) : null}
      </div>

      {viewerOnly ? (
        <div
          className="border-b px-4 py-1.5 text-center text-[11px]"
          style={{
            borderColor: 'rgba(201,169,97,0.55)',
            background: 'rgba(201,169,97,0.16)',
            color: '#8A5A08',
          }}
        >
          Read-only — you are not in the execution chain for this project.
        </div>
      ) : null}

      {/* Stats strip on the canvas top-right (mirrors the design's zone-stats). */}
      <div
        className="relative"
        style={{
          background: TOKEN.bg0,
          backgroundImage: `
            radial-gradient(ellipse at 50% 0%, rgba(201,169,97,0.08), transparent 50%),
            radial-gradient(ellipse at 50% 100%, rgba(31,90,71,0.05), transparent 50%)
          `,
        }}
      >
        {/* grid background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(11,59,46,0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(11,59,46,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />

        {/* zone stats top-right */}
        <div
          className="absolute right-4 top-3 z-10 flex gap-5"
          style={{ pointerEvents: 'none' }}
        >
          <div>
            <div
              className="text-[9px] uppercase"
              style={{ color: TOKEN.ink3, letterSpacing: '0.18em' }}
            >
              Members
            </div>
            <div
              className="text-[20px] font-medium tabular-nums"
              style={{ color: TOKEN.ink }}
            >
              {totalMembers}
            </div>
          </div>
          <div>
            <div
              className="text-[9px] uppercase"
              style={{ color: TOKEN.ink3, letterSpacing: '0.18em' }}
            >
              Departments
            </div>
            <div
              className="text-[20px] font-medium tabular-nums"
              style={{ color: TOKEN.ink }}
            >
              {departments.length}
            </div>
          </div>
        </div>

        {/* Canvas (horizontally scrolls if subtree wider than container) */}
        <div
          ref={canvasRef}
          className="relative w-full overflow-auto"
          style={{
            minHeight: layout.height + 40,
            maxHeight: isFullView ? 'calc(100vh - 100px)' : '78vh',
          }}
        >
          {/* Inner positioned canvas — width = layout.width to allow horizontal scroll */}
          <div
            className="relative"
            style={{ width: Math.max(containerW, layout.width), height: layout.height }}
          >
            {/* Zone labels along the left margin */}
            <ZoneLabel y={layout.yControl - 50} text="Control Layer · PMO" />
            <ZoneLabel y={layout.yDept - 84} text="Coordination Layer · Dept Heads & Managers" />
            <ZoneLabel y={layout.yEng - 84} text="Execution Layer · Site Engineers" />

            {/* Department containers — dotted box + light tinted fill, behind SVG */}
            {layout.departments.map((d) => (
              <div
                key={`box-${d.id}`}
                className="pointer-events-none absolute"
                style={{
                  left: d.boxLeft,
                  top: d.boxTop,
                  width: d.boxWidth,
                  height: d.boxHeight,
                  border: `1.5px dashed ${d.color}66`,
                  background: `${d.color}10`,
                  borderRadius: 16,
                  zIndex: 0,
                }}
              >
                {/* Dept name pill perched on the top edge — tight tracking so
                    full names fit; the tooltip carries the untruncated name */}
                <span
                  className="absolute inline-block truncate rounded-md border bg-[#FFFEFB] px-2.5 py-[3px] text-[10.5px] font-bold uppercase"
                  style={{
                    top: -12,
                    left: 14,
                    maxWidth: Math.max(150, d.boxWidth - 44),
                    color: d.color,
                    borderColor: `${d.color}55`,
                    letterSpacing: '0.07em',
                    boxShadow: '0 1px 2px rgba(11,59,46,0.08)',
                  }}
                  title={d.org.name}
                >
                  {d.org.name}
                </span>
                {/* "team count" tag tucked INSIDE the box (top-right) so it never
                    collides with the pill or the zone band label */}
                <span
                  className="absolute whitespace-nowrap rounded-md bg-[rgba(255,254,251,0.92)] px-1.5 py-[2px] text-[9.5px] font-semibold tabular-nums"
                  style={{
                    top: 7,
                    right: 8,
                    color: TOKEN.ink3,
                    border: `1px solid ${TOKEN.line}`,
                  }}
                >
                  {(d.head ? 1 : 0) +
                    d.managers.length +
                    d.managers.reduce((s, m) => s + m.engineers.length, 0)}{' '}
                  members · {d.managers.length}/{MAX_MANAGERS_PER_DEPT} mgr
                </span>
              </div>
            ))}

            {/* Manager-team sub-boxes (nested inside the department box) */}
            {layout.departments.flatMap((d) =>
              d.managers.map((m) => (
                <div
                  key={`mgrbox-${m.id}`}
                  className="pointer-events-none absolute"
                  style={{
                    left: m.boxLeft,
                    top: m.boxTop,
                    width: m.boxWidth,
                    height: m.boxHeight,
                    border: `1px dashed ${m.color}66`,
                    background: `${m.color}08`,
                    borderRadius: 12,
                    zIndex: 0,
                  }}
                >
                  {/* tiny "Team · <Manager>" pill on the box top edge */}
                  <span
                    className="absolute rounded border bg-[#FFFEFB] px-1.5 py-[1px] text-[9px] font-semibold uppercase"
                    style={{
                      top: -8,
                      left: 10,
                      color: m.color,
                      borderColor: `${m.color}55`,
                      letterSpacing: '0.06em',
                      fontFamily: '"Geist Mono", ui-monospace, monospace',
                    }}
                  >
                    Team · {(m.member.userName ?? 'Mgr').split(' ')[0]} · {m.engineers.length}/{MAX_ENGINEERS_PER_MANAGER}
                  </span>
                </div>
              )),
            )}

            {/* SVG: connectors + ambient particles */}
            <svg
              ref={svgRef}
              className="absolute inset-0"
              width={Math.max(containerW, layout.width)}
              height={layout.height}
              style={{ pointerEvents: 'none', zIndex: 1 }}
            >
              {/* PMO → Dept Heads */}
              {layout.primary
                ? layout.departments.map((d) => (
                    <path
                      key={`edge-pmo-${d.orgId}`}
                      id={`edge-pmo-${d.orgId}`}
                      d={curve(layout.primary!.x, layout.primary!.y + 28, d.x, d.y - 24)}
                      stroke={TOKEN.lineStrong}
                      strokeWidth={1}
                      fill="none"
                    />
                  ))
                : null}

              {/* Dept Head → Manager */}
              {layout.departments.flatMap((d) =>
                d.managers.map((m) => (
                  <path
                    key={`edge-mgr-${m.id}`}
                    id={`edge-mgr-${m.id}`}
                    d={curve(d.x, d.y + 24, m.x, m.y - 18)}
                    stroke={TOKEN.lineStrong}
                    strokeWidth={1}
                    fill="none"
                  />
                )),
              )}

              {/* Manager → Engineers */}
              {layout.departments.flatMap((d) =>
                d.managers.flatMap((m) =>
                  m.engineers.map((e) => (
                    <path
                      key={`edge-eng-${e.id}`}
                      id={`edge-eng-${e.id}`}
                      d={curve(m.x, m.y + 18, e.x, e.y - 16)}
                      stroke={TOKEN.line}
                      strokeWidth={1}
                      fill="none"
                    />
                  )),
                ),
              )}

              {/* Ambient drifting particles */}
              <AmbientParticles particles={particles} svgRef={svgRef} />
            </svg>

            {/* PMO node (dark, central) */}
            {layout.primary ? (
              <AbsCard
                x={layout.primary.x}
                y={layout.primary.y}
                onMouseEnter={() =>
                  showTip(
                    layout.primary!.x,
                    layout.primary!.y - 36,
                    layout.primary!.member,
                    ROLE_LABEL[layout.primary!.member.role],
                  )
                }
                onMouseLeave={hideTip}
                onClick={() => onCardClick?.(layout.primary!.member)}
                className="cursor-pointer"
                style={{
                  minWidth: 200,
                  borderRadius: 10,
                  padding: '9px 14px',
                  background: '#0B3B2E',
                  color: '#fff',
                  border: '1px solid rgba(11,59,46,0.45)',
                  borderLeft: `3px solid ${TOKEN.accent}`,
                  boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 16px 40px -24px rgba(11,59,46,0.20)',
                  textAlign: 'center',
                }}
              >
                <div className="truncate text-[13px] font-bold" style={{ letterSpacing: '-0.01em' }}>
                  {layout.primary.member.userName ?? 'Unknown'}
                </div>
                <div
                  className="truncate text-[9.5px] font-semibold uppercase"
                  style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.08em' }}
                >
                  {ROLE_LABEL[layout.primary.member.role]}
                </div>
                <div
                  className="mt-1.5 flex justify-center gap-3 border-t pt-1.5 text-[9px]"
                  style={{ borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}
                >
                  <div>
                    Members: <b style={{ color: '#fff' }}>{totalMembers}</b>
                  </div>
                  <div>
                    Depts: <b style={{ color: '#fff' }}>{departments.length}</b>
                  </div>
                </div>
                {canRemoveMember(layout.primary.member) ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      askConfirm({
                        kind: 'member',
                        member: layout.primary!.member,
                        roleLabel: ROLE_LABEL[layout.primary!.member.role],
                      });
                    }}
                    title="Remove from team"
                    className="absolute right-1 top-1 rounded p-0.5 opacity-60 transition-opacity hover:bg-white/10 hover:opacity-100"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                ) : null}
              </AbsCard>
            ) : null}

            {/* Governance satellites (Project Head, Coordinator, etc.) */}
            {layout.satellites.map((g) => (
              <AbsCard
                key={g.id}
                x={g.x}
                y={g.y}
                onMouseEnter={() => showTip(g.x, g.y - 24, g.member, ROLE_LABEL[g.member.role])}
                onMouseLeave={hideTip}
                onClick={() => onCardClick?.(g.member)}
                className="cursor-pointer"
                style={{
                  width: 150,
                  borderRadius: 10,
                  padding: '7px 12px',
                  background: TOKEN.bg1,
                  border: `1px solid ${TOKEN.line}`,
                  borderLeft: '3px solid #0B3B2E',
                  boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 8px 20px -14px rgba(11,59,46,0.18)',
                  textAlign: 'center',
                }}
              >
                <div
                  className="truncate text-[11.5px] font-semibold"
                  style={{ color: TOKEN.ink }}
                >
                  {g.member.userName ?? 'Unknown'}
                </div>
                <div
                  className="truncate text-[9.5px] uppercase"
                  style={{ color: TOKEN.ink3, letterSpacing: '0.06em' }}
                >
                  {ROLE_LABEL[g.member.role]}
                </div>
              </AbsCard>
            ))}

            {/* Add-Governance slot near the PMO row */}
            {showGovernanceChrome && canGovernanceAdd ? (
              <VacantSlot
                x={(layout.primary?.x ?? containerW / 2) + 200 + layout.satellites.length * 30}
                y={layout.yControl}
                label="Add governance"
                interactive
                onClick={() => onIntent({ kind: 'governance' })}
                disabled={saving}
              />
            ) : null}

            {/* Department heads */}
            {layout.departments.map((d) => (
              <AbsCard
                key={d.id}
                x={d.x}
                y={d.y}
                onMouseEnter={() =>
                  d.head ? showTip(d.x, d.y - 22, d.head, ROLE_LABEL[d.head.role], [
                    { k: 'Department', v: d.org.name },
                    { k: 'Managers', v: String(d.managers.length) },
                  ]) : null
                }
                onMouseLeave={hideTip}
                onClick={() => d.head && onCardClick?.(d.head)}
                className={d.head ? 'cursor-pointer' : ''}
                style={{
                  width: 188,
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: TOKEN.bg1,
                  border: `1px solid ${TOKEN.line}`,
                  borderLeft: `3px solid ${d.color}`,
                  boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 8px 20px -14px rgba(11,59,46,0.18)',
                  textAlign: 'center',
                }}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[12.5px] font-bold"
                      style={{ color: TOKEN.ink, letterSpacing: '-0.01em' }}
                      title={d.head?.userName ?? undefined}
                    >
                      {d.head?.userName ?? `${d.org.name} — vacant`}
                    </div>
                    <div
                      className="truncate text-[9.5px] font-semibold uppercase"
                      style={{ color: TOKEN.ink3, letterSpacing: '0.06em' }}
                      title={`${d.org.name} Head`}
                    >
                      {d.org.name} Head · {d.head ? '1/1' : '0/1'}
                    </div>
                  </div>
                  {showEditFocus && onEditFocus ? (
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onEditFocus(d.org);
                      }}
                      title="Edit focus areas"
                      className="shrink-0 rounded p-0.5 hover:bg-[#F2EADB]"
                      style={{ color: TOKEN.ink3 }}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  ) : null}
                  {showEditFocus && onRemoveDepartment ? (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        const memberCount =
                          (d.head ? 1 : 0) +
                          d.managers.length +
                          d.managers.reduce((s, m) => s + m.engineers.length, 0);
                        askConfirm({ kind: 'department', org: d.org, memberCount });
                      }}
                      title="Remove department"
                      className="shrink-0 rounded p-0.5 hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239]"
                      style={{ color: TOKEN.ink3 }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
                {d.head ? null : (
                  <div className="mt-1.5">
                    {canAddHead(d.orgId) ? (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onIntent({ kind: 'department_head', orgId: d.orgId });
                        }}
                        className="w-full rounded-md border border-dashed px-2 py-1 text-[11px] font-medium hover:bg-[#F2EADB] disabled:opacity-40"
                        style={{ borderColor: TOKEN.lineStrong, color: TOKEN.ink2 }}
                      >
                        <Plus className="mr-1 inline-block h-3 w-3 align-[-2px]" /> Assign head
                      </button>
                    ) : (
                      <span className="text-[11px]" style={{ color: TOKEN.ink3 }}>
                        Awaiting governance
                      </span>
                    )}
                  </div>
                )}
                {d.head && canRemoveMember(d.head) ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      askConfirm({
                        kind: 'member',
                        member: d.head!,
                        roleLabel: ROLE_LABEL[d.head!.role],
                        deptName: d.org.name,
                      });
                    }}
                    title="Remove head from team"
                    className="absolute right-1 top-1 rounded p-0.5 opacity-60 transition-opacity hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239] hover:opacity-100"
                    style={{ color: TOKEN.ink3 }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                ) : null}
              </AbsCard>
            ))}

            {/* Manager nodes */}
            {layout.departments.flatMap((d) =>
              d.managers.map((m) => (
                <AbsCard
                  key={m.id}
                  x={m.x}
                  y={m.y}
                  onMouseEnter={() =>
                    showTip(m.x, m.y - 20, m.member, ROLE_LABEL[m.member.role], [
                      { k: 'Department', v: d.org.name },
                      { k: 'Engineers', v: String(m.engineers.length) },
                    ])
                  }
                  onMouseLeave={hideTip}
                  onClick={() => onCardClick?.(m.member)}
                  className="cursor-pointer"
                  style={{
                    width: 168,
                    borderRadius: 10,
                    padding: '7px 10px',
                    background: TOKEN.bg1,
                    border: `1px solid ${TOKEN.line}`,
                    borderLeft: `3px solid ${m.color}`,
                    boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 8px 20px -14px rgba(11,59,46,0.18)',
                    textAlign: 'center',
                  }}
                >
                  <div
                    className="truncate text-[12px] font-bold"
                    style={{ color: TOKEN.ink, letterSpacing: '-0.01em' }}
                    title={m.member.userName ?? undefined}
                  >
                    {m.member.userName ?? 'Unknown'}
                  </div>
                  <div
                    className="truncate text-[9.5px] font-semibold uppercase"
                    style={{ color: TOKEN.ink3, letterSpacing: '0.06em' }}
                    title={`${d.org.name} Manager`}
                  >
                    {d.org.name} Manager
                  </div>
                  {canRemoveMember(m.member) ? (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        askConfirm({
                          kind: 'member',
                          member: m.member,
                          roleLabel: ROLE_LABEL[m.member.role],
                          deptName: d.org.name,
                        });
                      }}
                      title="Remove manager from team"
                      className="absolute right-0.5 top-0.5 rounded p-0.5 opacity-50 transition-opacity hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239] hover:opacity-100"
                      style={{ color: TOKEN.ink3 }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </AbsCard>
              )),
            )}

            {/* Add-Manager slots */}
            {layout.departments.map((d) =>
              d.addManagerAt ? (
                <VacantSlot
                  key={`addmgr-${d.id}`}
                  x={d.addManagerAt.x}
                  y={d.addManagerAt.y}
                  width={130}
                  label="Add manager"
                  vacantLabel="Vacant · manager"
                  capacity={`${d.managers.length}/${MAX_MANAGERS_PER_DEPT}`}
                  interactive={d.addManagerCanAct}
                  disabled={saving}
                  onClick={() =>
                    d.head
                      ? onIntent({ kind: 'manager', orgId: d.orgId, reportsToUserId: d.head.userId })
                      : undefined
                  }
                />
              ) : null,
            )}

            {/* Engineer nodes */}
            {layout.departments.flatMap((d) =>
              d.managers.flatMap((m) =>
                m.engineers.map((e) => {
                  const multi = (memberDeptCountMap.get(e.member.userId) ?? 1) > 1;
                  return (
                    <AbsCard
                      key={e.id}
                      x={e.x}
                      y={e.y}
                      onMouseEnter={() =>
                        showTip(e.x, e.y - 18, e.member, ROLE_LABEL[e.member.role], [
                          { k: 'Reports to', v: m.member.userName ?? '—' },
                          { k: 'Department', v: d.org.name },
                        ])
                      }
                      onMouseLeave={hideTip}
                      onClick={() => onCardClick?.(e.member)}
                      className="cursor-pointer"
                      style={{
                        minWidth: ENG_W,
                        borderRadius: 8,
                        padding: '6px 8px',
                        background: TOKEN.bg1,
                        border: `1px solid ${TOKEN.line}`,
                        borderLeft: `3px solid ${e.color}`,
                        boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 8px 20px -14px rgba(11,59,46,0.18)',
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <div
                          className="min-w-0 flex-1 truncate text-[11px] font-semibold"
                          style={{ color: TOKEN.ink }}
                        >
                          {e.member.userName ?? 'Unknown'}
                        </div>
                        <DeviceFrame tone="active" />
                      </div>
                      {/* status dot */}
                      <span
                        className="absolute"
                        style={{
                          top: -3,
                          right: -3,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: TOKEN.blue,
                          border: `2px solid ${TOKEN.bg0}`,
                          animation: 'epc-pulse 1.4s infinite',
                        }}
                      />
                      {multi ? (
                        <span
                          className="absolute rounded px-1 text-[9px] font-semibold"
                          style={{
                            bottom: -8,
                            left: 8,
                            background: 'rgba(201,169,97,0.16)',
                            color: '#8A5A08',
                            border: '1px solid rgba(201,169,97,0.55)',
                          }}
                          title={`In ${memberDeptCountMap.get(e.member.userId)} departments`}
                        >
                          ×{memberDeptCountMap.get(e.member.userId)}
                        </span>
                      ) : null}
                      {canRemoveMember(e.member) ? (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            askConfirm({
                              kind: 'member',
                              member: e.member,
                              roleLabel: ROLE_LABEL[e.member.role],
                              deptName: d.org.name,
                            });
                          }}
                          title="Remove engineer from team"
                          className="absolute right-0.5 top-0.5 rounded p-0.5 opacity-50 transition-opacity hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239] hover:opacity-100"
                          style={{ color: TOKEN.ink3 }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      ) : null}
                    </AbsCard>
                  );
                }),
              ),
            )}

            {/* Add-Engineer slots — share the same staggered grid as engineers */}
            {layout.departments.flatMap((d) =>
              d.managers
                .filter((m) => !!m.addEngineerAt)
                .map((m) => (
                  <VacantSlot
                    key={`addeng-${m.id}`}
                    x={m.addEngineerAt!.x}
                    y={m.addEngineerAt!.y}
                    width={ENG_W}
                    label="Add engineer"
                    vacantLabel="Vacant · engineer"
                    capacity={`${m.engineers.length}/${MAX_ENGINEERS_PER_MANAGER}`}
                    interactive={m.addEngineerCanAct}
                    disabled={saving}
                    onClick={() =>
                      onIntent({ kind: 'site_engineer', orgId: d.orgId, managerUserId: m.member.userId })
                    }
                  />
                )),
            )}

            {/* Empty state */}
            {departments.length === 0 ? (
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center"
                style={{ color: TOKEN.ink3 }}
              >
                <Building2 className="mx-auto mb-2 h-8 w-8 opacity-40" />
                <p className="text-[12.5px] font-semibold" style={{ color: TOKEN.ink2 }}>
                  No departments yet
                </p>
                {showAddDepartmentButton ? (
                  <p className="mt-1 text-[11px]">Click "Add Department" to create one.</p>
                ) : null}
              </div>
            ) : null}

            {/* Tooltip overlay */}
            <Tooltip hover={hover} />
          </div>
        </div>

        {/* Caption banner — bottom centred, mirrors the design's `.caption` */}
        <div
          className="absolute left-1/2 z-10 inline-flex max-w-[70%] -translate-x-1/2 items-center gap-3 rounded-full px-4 py-2 text-[11.5px] text-white"
          style={{
            bottom: 14,
            background: TOKEN.ink,
            boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 16px 40px -24px rgba(11,59,46,0.30)',
          }}
        >
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-mono tracking-wider"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <Activity className="mr-1 inline h-3 w-3 align-[-2px]" />
            LIVE
          </span>
          <span>
            PMO → {departments.length} department{departments.length === 1 ? '' : 's'} · curved flows show
            assignment + reporting
          </span>
        </div>
      </div>

      {/* ── Confirmation drawer (right side) ── */}
      {confirmTarget ? (
        <>
          {/* backdrop */}
          <div
            className="fixed inset-0 z-[300]"
            style={{ background: 'rgba(11,59,46,0.40)' }}
            onClick={closeConfirm}
            aria-hidden
          />
          {/* drawer */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="epc-team-confirm-title"
            className="fixed bottom-0 right-0 top-0 z-[301] flex w-full max-w-[380px] flex-col border-l bg-[#FFFEFB] shadow-2xl"
            style={{ borderColor: TOKEN.line, animation: 'epc-drawer-in 0.18s ease-out' }}
          >
            {/* header */}
            <div
              className="flex items-start justify-between gap-3 border-b px-4 py-3"
              style={{ borderColor: TOKEN.line }}
            >
              <div className="flex items-start gap-2">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
                  style={{ background: 'rgba(185,28,28,0.10)', color: TOKEN.bad }}
                >
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3
                    id="epc-team-confirm-title"
                    className="text-[13px] font-semibold"
                    style={{ color: TOKEN.ink }}
                  >
                    {confirmTarget.kind === 'department' ? 'Remove department?' : 'Remove from team?'}
                  </h3>
                  <p className="text-[11px]" style={{ color: TOKEN.ink3 }}>
                    This action can be reversed by re-activating later.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeConfirm}
                className="rounded p-1 hover:bg-[#F2EADB]"
                style={{ color: TOKEN.ink3 }}
                aria-label="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* body */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {confirmTarget.kind === 'member' ? (
                <>
                  <div
                    className="rounded-lg border p-3"
                    style={{ borderColor: TOKEN.line, background: TOKEN.bg2 }}
                  >
                    <div className="text-[12.5px] font-semibold" style={{ color: TOKEN.ink }}>
                      {confirmTarget.member.userName ?? 'Unknown'}
                    </div>
                    <div
                      className="mt-0.5 text-[10.5px] uppercase"
                      style={{ color: TOKEN.ink3, letterSpacing: '0.06em' }}
                    >
                      {confirmTarget.roleLabel}
                      {confirmTarget.deptName ? ` · ${confirmTarget.deptName}` : ''}
                    </div>
                    {confirmTarget.member.userEmail ? (
                      <div className="mt-1 text-[11px]" style={{ color: TOKEN.ink2 }}>
                        {confirmTarget.member.userEmail}
                      </div>
                    ) : null}
                  </div>
                  <p
                    className="mt-3 text-[12px] leading-relaxed"
                    style={{ color: TOKEN.ink2 }}
                  >
                    Removing this member will deactivate them on this project. Their assignments
                    stay in history; new tasks won't route to them.
                  </p>
                </>
              ) : (
                <>
                  <div
                    className="rounded-lg border p-3"
                    style={{ borderColor: TOKEN.line, background: TOKEN.bg2 }}
                  >
                    <div className="text-[12.5px] font-semibold" style={{ color: TOKEN.ink }}>
                      {confirmTarget.org.name}
                    </div>
                    <div
                      className="mt-0.5 text-[10.5px] uppercase"
                      style={{ color: TOKEN.ink3, letterSpacing: '0.06em' }}
                    >
                      Department · {confirmTarget.memberCount}{' '}
                      {confirmTarget.memberCount === 1 ? 'member' : 'members'}
                    </div>
                  </div>
                  <p
                    className="mt-3 text-[12px] leading-relaxed"
                    style={{ color: TOKEN.ink2 }}
                  >
                    Removing this department deactivates the org unit. Heads, managers and
                    engineers under it are kept in history but excluded from new task routing.
                  </p>
                  {confirmTarget.memberCount > 0 ? (
                    <div
                      className="mt-3 flex items-start gap-2 rounded-md border p-2.5 text-[11.5px]"
                      style={{
                        borderColor: 'rgba(201,169,97,0.55)',
                        background: 'rgba(201,169,97,0.16)',
                        color: '#8A5A08',
                      }}
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        <b>{confirmTarget.memberCount}</b>{' '}
                        {confirmTarget.memberCount === 1 ? 'person is' : 'people are'} currently
                        assigned to this department. Re-assign them before removing if you want
                        them to keep working on this project.
                      </span>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* footer */}
            <div
              className="flex items-center justify-end gap-2 border-t px-4 py-3"
              style={{ borderColor: TOKEN.line, background: TOKEN.bg0 }}
            >
              <button
                type="button"
                onClick={closeConfirm}
                className="rounded-md border px-3 py-1.5 text-[12px] font-medium hover:bg-[#F2EADB]"
                style={{ borderColor: TOKEN.line, background: TOKEN.bg1, color: TOKEN.ink2 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ background: TOKEN.bad }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {confirmTarget.kind === 'department' ? 'Remove department' : 'Remove member'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {/* keyframes (scoped) */}
      <style>{`
        @keyframes epc-livepulse {
          0%   { box-shadow: 0 0 0 0 rgba(51,146,116,0.6); }
          80%  { box-shadow: 0 0 0 8px rgba(51,146,116,0); }
          100% { box-shadow: 0 0 0 0 rgba(51,146,116,0); }
        }
        @keyframes epc-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.3); opacity: 0.7; }
        }
        @keyframes epc-drawer-in {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ───────────────────────── ZONE LABEL ── */

function ZoneLabel({ y, text }: { y: number; text: string }) {
  return (
    <div
      className="absolute flex items-center gap-2.5 text-[9.5px] font-bold uppercase"
      style={{
        left: 24,
        top: y,
        color: TOKEN.ink3,
        letterSpacing: '0.14em',
        fontFamily: '"Geist Mono", ui-monospace, monospace',
        // paper-coloured halo keeps the band label legible where boxes pass under it
        textShadow: '0 1px 0 rgba(248,242,226,0.95), 0 -1px 0 rgba(248,242,226,0.95), 1px 0 0 rgba(248,242,226,0.95), -1px 0 0 rgba(248,242,226,0.95)',
      }}
    >
      {text}
      <span className="block" style={{ width: 60, height: 1, background: TOKEN.line }} />
    </div>
  );
}
